/* eslint max-lines: off */
import { createMuiTheme } from '@material-ui/core/styles'
import Chart from 'chart.js'

export const COLORS = {
  BLACK: '#000',
  BUTTON_BACKGROUND: 'rgba(49, 55, 67, 0.07)',
  CONTENT_BACKGROUND: '#fbfcff',
  DROPBOX: '#007ee5',
  ERROR_LIGHT: 'rgba(255, 45, 79, 0.15)',
  ERROR: '#ff2d4f',
  GOOGLE: '#4285f4',
  IMAGE_OVERLAY: 'rgba(0, 0, 0, 0.6)',
  INPUT_BORDER: '#ced4da',
  OFFICE: '#ea3e23',
  PLACEHOLDER_TEXT: 'rgba(0, 0, 0, 0.87)',
  PRIMARY_BORDER_COLOR_LIGHT: 'rgba(49, 55, 67, 0.15)',
  PRIMARY_BORDER_COLOR: 'rgba(49, 55, 67, 0.1)',
  PRIMARY_DARK: '#0069d8',
  PRIMARY_LIGHT: 'rgb(0, 122, 250,0.15)',
  PRIMARY_TEXT: '#313743',
  PRIMARY: '#007afa',
  SECONDARY_BORDER_COLOR: 'rgba(0, 0, 0, 0.1)',
  SECONDARY_LIGHT: 'rgba(49, 55, 67, 0.66)',
  SECONDARY_ULTRA_LIGHT: 'rgba(49, 55, 67, 0.33)',
  SECONDARY: '#313743',
  SELECT_HOVER: '#DEEBFF',
  SKELETON_BACKGROUND: '#E1E1E1',
  SUCCESS_LIGHT: 'rgba(33, 189, 68, 0.15)',
  SUCCESS: '#21bd44',
  TABLE_HEADER_BACKGROUND: 'rgb(250, 250, 250)',
  TABLE_ROW_HOVER_BACKGROUND: 'rgba(0, 0, 0, 0.07)',
  WARNING: '#FFC400',
  WHITE: '#fff',
}

export const GRAPH_COLORS = {
  LABEL_TEXT: COLORS.SECONDARY,
  LEGEND_TEXT: COLORS.SECONDARY,
  INVOICE_STROKE: COLORS.SUCCESS,
  RECEIPT_STROKE: COLORS.PRIMARY,
  HOTEL_FOLIO_STROKE: COLORS.ERROR,
  INVOICE_AREA: COLORS.SUCCESS_LIGHT,
  GRID_STROKE: 'rgba(0, 0, 0, 0.08)',
  RECEIPT_AREA: COLORS.PRIMARY_LIGHT,
  HOTEL_FOLIO_AREA: COLORS.ERROR_LIGHT,
}

export const TAG_COLORS = {
  PAID: COLORS.SUCCESS,
  UNPAID: COLORS.ERROR,
  PENDING: COLORS.PRIMARY,
  UNPAID_BACKGROUND: COLORS.ERROR_LIGHT,
  PAID_BACKGROUND: COLORS.SUCCESS_LIGHT,
  PENDING_BACKGROUND: COLORS.PRIMARY_LIGHT,
}

export const styleProperties = {
  INPUT_TEXT_BOX_SHADOW: `0 0 0 1px ${COLORS.PRIMARY}`,
  INPUT_TEXT_ERROR_BOX_SHADOW: `0 0 0 1px ${COLORS.ERROR}`,
  INPUT_TEXT_SUCCESS_BOX_SHADOW: `0 0 0 1px ${COLORS.SUCCESS}`,
}

export const createTheme = (settings = {}) => {
  const { darkMode } = settings

  const theme = createMuiTheme({
    breakpoints: {
      'nav-sm': 760,
      'nav-md': 1060,
    },
    dimensions: {
      containerMaxWidth: 1500,
      headerHeight: 56,
      largeLoaderHeight: 8,
      sidebarWidthCollapsed: 70,
      sidebarWidthExpanded: 240,
      smallLoaderHeight: 6,
    },
    palette: {
      border: {
        primary: 'rgba(224, 224, 224, 1)',
        secondary: 'rgba(0, 0, 0, 0.1)',
      },
      primary: {
        main: '#007afa',
        dark: '#007afa',
        light: 'rgb(0, 122, 250,0.15)',
        medium: '#007afa99',
      },
      secondary: {
        main: '#313743',
        light: 'rgba(49, 55, 67, 0.33)',
      },
      error: {
        main: '#ff2d4f',
        light: 'rgba(255, 45, 79, 0.15)',
        medium: '#ff2d4f99',
      },
      success: {
        main: '#21bd44',
        light: 'rgba(33, 189, 68, 0.15)',
      },
      table: {
        headBackground: 'rgb(250, 250, 250)',
        rowHoverBackground: 'rgba(0, 0, 0, 0.07)',
      },
      type: darkMode ? 'dark' : 'light',
    },
    spacing: 4,
  })

  const { palette, spacing, typography, zIndex } = theme

  // Mutate overrides & props objects after theme variable is declared
  // so we have access to the palette object in the overrides
  theme.overrides = {
    MuiButton: {
      // root: {
      //   height: 44,
      //   paddingLeft: spacing(6),
      //   paddingRight: spacing(6),
      //   borderRadius: 22,
      // },
      containedSizeSmall: {
        height: 36,
        paddingRight: spacing(3),
        paddingLeft: spacing(3),
        textTransform: 'capitalize',
      },
    },
    MuiCssBaseline: {
      '@global': {
        a: {
          color: 'inherit',
          textDecoration: 'none',
        },
        '.intercom-lightweight-app': {
          zIndex: `${zIndex.appBar} !important`,
        },
      },
    },
    MuiDivider: {
      root: {
        width: '100%',
      },
    },
    MuiExpansionPanel: {
      root: {
        '&::before': {
          display: 'none',
        },
      },
    },
    MuiExpansionPanelDetails: {
      root: {
        padding: 0,
      },
    },
    MuiLinearProgress: {
      root: {
        width: '100%',
        height: 8,
      },
    },
    MuiMenuItem: {
      root: {
        borderLeft: '4px solid transparent',
        borderRight: '4px solid transparent',
        '&:hover': {
          backgroundColor: `${palette.primary.main}33`,
          borderLeft: `4px solid ${palette.primary.main}`,
        },
      },
    },
    MuiOutlinedInput: {
      root: {
        '& $notchedOutline': {
          borderRadius: 2,
          borderColor: '#ced4da',
        },
        '&:hover:not($disabled):not($focused):not($error) $notchedOutline': {
          borderColor: '#ced4da',
        },
      },
    },
    MuiTab: {
      wrapper: {
        fontWeight: 600,
        textTransform: 'capitalize',
      },
    },
    MuiTableCell: {
      head: {
        padding: spacing(3, 4),
      },
    },
    MuiTableSortLabel: {
      root: {
        color: palette.secondary.light,
        fontFamily: '"Roboto", sans-serif',
        fontWeight: typography.fontWeightBold,
        fontSize: typography.caption.fontSize,
        textTransform: 'uppercase',
      },
    },
    MuiTextField: {
      root: {
        'label + &': {
          marginTop: spacing(3),
        },
      },
    },
    MuiToggleButton: {
      root: {
        '&.Mui-selected': {
          color: palette.primary.contrastText,
          backgroundColor: palette.primary.main,
          '&:hover': {
            // TODO: Figure out why override not working correctly
            backgroundColor: `${palette.primary.main}DD !important`,
          },
        },
      },
    },
  }

  theme.props = {
    MuiExpansionPanel: {
      elevation: 0,
      square: true,
    },
  }

  Chart.defaults.global.defaultFontFamily = theme.typography.fontFamily

  const fontColor = palette.type === 'dark' ? palette.primary.contrastText : palette.text.primary

  theme.areaChartOptions = {
    elements: {
      line: {
        tension: 0.075,
      },
    },
    layout: {
      padding: spacing(3),
    },
    legend: {
      display: false,
    },
    maintainAspectRatio: false,
    scales: {
      xAxes: [
        {
          gridLines: {
            zeroLineColor: 'rgba(0,0,0,0.1)',
          },
          type: 'time',
          ticks: {
            fontColor,
            padding: spacing(3),
          },
          time: {
            unit: 'day',
            unitStepSize: 1,
            displayFormats: {
              day: 'MM/DD/YY',
              hour: 'MM/DD HH:mm',
            },
          },
        },
      ],
      yAxes: [
        {
          gridLines: {
            drawTicks: false,
          },
          ticks: {
            beginAtZero: true,
            fontColor,
            padding: spacing(3),
            precision: 0,
          },
        },
      ],
    },
    tooltips: {
      enabled: false,
    },
  }

  theme.donutChartOptions = {
    layout: {
      padding: spacing(3),
    },
    legend: {
      display: false,
    },
    maintainAspectRatio: false,
    tooltips: {
      enabled: false,
    },
  }

  return theme
}

export const getGridLinesOptions = ({ palette }) => {
  const gridLineColor =
    palette.type === 'dark' ? `${palette.primary.contrastText}99` : palette.text.disabled
  return {
    xAxes: [
      {
        gridLines: {
          color: gridLineColor,
        },
      },
    ],
    yAxes: [
      {
        gridLines: {
          color: gridLineColor,
        },
      },
    ],
  }
}

export const timeHours = {
  scales: {
    xAxes: [
      {
        time: {
          unit: 'hours',
        },
      },
    ],
  },
}

export const timeDays = {
  scales: {
    xAxes: [
      {
        time: {
          unit: 'day',
        },
      },
    ],
  },
}
